function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import sortBy from 'lodash/sortBy';
import { encrypt, generateKeyPair, generateSecret, getKeyFromPem, getPrvKeyPem, getPubKeyPem } from 'helpers/crypto';
import { serializeQuery } from 'helpers/urlTools';
import AlertService from 'services/AlertService';
import ApiService from 'services/ApiService';
import OidcService from 'services/OidcService';
import App from 'modules/App';
import Account from 'modules/Account';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import messages from './messages';
function* fetchOrganizations({ payload }) {
    const { queryParams } = payload;
    const searchParams = serializeQuery(queryParams, {
        omitFalsy: true
    });
    try {
        const requestUrl = `/api/Organization?${searchParams}`;
        const { records, totalRecords } = yield call(ApiService.regionalRequest, requestUrl);
        yield put(actions.fetchOrganizationsSuccess(records, totalRecords));
    } catch (err) {
        yield put(actions.fetchOrganizationsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchOrganizationMemberships() {
    try {
        const requestUrl = '/api/OrganizationMembership';
        let organizationMemberships = yield call(ApiService.regionalRequest, requestUrl);
        if (organizationMemberships && organizationMemberships.length) {
            organizationMemberships = sortBy(organizationMemberships, 'organization.name');
        }
        yield put(actions.fetchOrganizationMembershipsSuccess(organizationMemberships));
    } catch (err) {
        yield put(actions.fetchOrganizationMembershipsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* fetchOrganizationMembershipDetails({ payload }) {
    try {
        const { organizationUID, countryCode } = payload.organizationMembership.organization;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = `/api/OrganizationMembership/${organizationUID}/Details`;
        const organizationMembershipDetails = yield call(ApiService.crossRegionalRequest, region, requestUrl);
        yield put(actions.fetchOrganizationMembershipDetailsSuccess(organizationMembershipDetails));
    } catch (err) {
        yield put(actions.fetchOrganizationMembershipDetailsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchOrganizationTemplate({ payload }) {
    try {
        let { countryCode } = payload;
        if (!countryCode) {
            countryCode = yield call(OidcService.getCountryCode);
        }
        const requestUrl = `/api/Information/Organization/Template?countryCode=${countryCode}`;
        const organizationTemplate = yield call(ApiService.regionalRequest, requestUrl);
        yield put(actions.fetchOrganizationTemplateSuccess(organizationTemplate, countryCode));
    } catch (err) {
        yield put(actions.fetchOrganizationTemplateError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* generateOrganizationKeys() {
    const accountPublicKey = yield select(Account.selectors.publicKey);
    const accountPublicKeyObj = getKeyFromPem(accountPublicKey);
    const passphrase = yield call(generateSecret);
    const encryptedPassphrase = encrypt(passphrase, accountPublicKeyObj);
    const { prvKeyObj, pubKeyObj } = yield call(generateKeyPair);
    const [encryptedPrivateKey, publicKey] = yield all([
        call(getPrvKeyPem, prvKeyObj, passphrase),
        call(getPubKeyPem, pubKeyObj)
    ]);
    return {
        encryptedPassphrase,
        encryptedPrivateKey,
        publicKey
    };
}
function* reFetchOrganizationMemberships() {
    yield all([
        put(actions.fetchOrganizationMemberships()),
        put(Account.actions.fetchOrganizationMemberships())
    ]);
    const [fetchOrganizationMembershipsResult, fetchAccountOrganizationMembershipsResult] = yield all([
        take([
            actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_SUCCESS,
            actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR
        ]),
        take([
            Account.actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_SUCCESS,
            Account.actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR
        ])
    ]);
    if (fetchOrganizationMembershipsResult.type === actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR) {
        throw fetchOrganizationMembershipsResult.error;
    }
    if (fetchAccountOrganizationMembershipsResult.type === Account.actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR) {
        throw fetchAccountOrganizationMembershipsResult.error;
    }
}
function* createOrganization({ payload }) {
    const holderEntry = yield select(Account.selectors.holderEntry);
    try {
        const { countryCode } = payload;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const { encryptedPassphrase, encryptedPrivateKey, publicKey } = yield call(generateOrganizationKeys);
        const requestUrl = '/api/Organization';
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: _object_spread_props(_object_spread({}, payload), {
                publicKey,
                organizationMembershipDetails: _object_spread_props(_object_spread({}, payload.organizationMembershipDetails), {
                    encryptedPassphrase,
                    encryptedPrivateKey
                }),
                holderEntry
            })
        });
        yield put(Account.actions.fetchOrganizationMemberships());
        yield call(reFetchOrganizationMemberships);
        yield put(actions.createOrganizationSuccess());
    } catch (err) {
        yield put(actions.createOrganizationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* joinOrganization({ payload }) {
    const holderEntry = yield select(Account.selectors.holderEntry);
    try {
        const { organization, organizationMembershipDetails } = payload;
        const { organizationUID, countryCode } = organization;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = '/api/OrganizationMembership';
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: {
                organizationUID,
                organizationMembershipDetails,
                holderEntry
            }
        });
        yield call(reFetchOrganizationMemberships);
        yield put(actions.joinOrganizationSuccess());
    } catch (err) {
        yield put(actions.joinOrganizationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* leaveOrganization({ payload }) {
    try {
        const { organizationMembership } = payload;
        const { organizationUID, organization } = organizationMembership;
        const region = yield call(ApiService.findRegionByCountry, organization.countryCode);
        const requestUrl = `/api/OrganizationMembership/${organizationUID}`;
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'DELETE'
        });
        yield call(reFetchOrganizationMemberships);
        yield put(actions.leaveOrganizationSuccess(organizationMembership));
        const alert = yield call(AlertService.createAlertDispatcher);
        yield alert.success(messages.alerts.leaveOrganizationSuccess);
    } catch (err) {
        yield put(actions.leaveOrganizationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* sagas() {
    yield takeLatest(actionTypes.FETCH_ORGANIZATIONS, fetchOrganizations);
    yield takeLatest(actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS, fetchOrganizationMemberships);
    yield takeLatest(actionTypes.FETCH_ORGANIZATION_MEMBERSHIP_DETAILS, fetchOrganizationMembershipDetails);
    yield takeLatest(actionTypes.FETCH_ORGANIZATION_TEMPLATE, fetchOrganizationTemplate);
    yield takeLatest(actionTypes.CREATE_ORGANIZATION, createOrganization);
    yield takeLatest(actionTypes.JOIN_ORGANIZATION, joinOrganization);
    yield takeLatest(actionTypes.LEAVE_ORGANIZATION, leaveOrganization);
}
export default [
    sagas
];
